import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { fetchPatients } from "../../core/redux/slices/patientSlice";
import { getDailyPatientReport } from "../../core/api/reportService";
import StickyScrollTable from "../components/StickyScrollTable";
import { exportToCsv } from "../../core/utils/exportCsv";
import { formatAge } from "../../core/utils/calculateAge";
import { toId } from "../../core/utils/ids";
import { getApiErrorMessage } from "../../core/utils/apiError";

const today = () => new Date().toISOString().split("T")[0];
const fmt = (v) => (v ? Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "-");

// GET /api/reports/daily-patient?date= (HANDOVER_FRONTEND 2.md) returns one row per
// invoice generated that day, with treatment/shades/tooth already comma-joined and
// payments already summed by mode server-side — no client-side aggregation needed.
const DailyReport = () => {
  const dispatch = useDispatch();
  const patients = useSelector((s) => s.patients.list);

  const [date, setDate] = useState(today());
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { dispatch(fetchPatients()); }, [dispatch]);

  const patientById = useMemo(() => {
    const m = new Map();
    patients.forEach((p) => { const k = toId(p.patientID || p.id); if (k) m.set(k.toLowerCase(), p); });
    return m;
  }, [patients]);
  // GUIDs compare case-insensitively (see utils/ids).
  const patientFor = (r) => patientById.get((toId(r.patientID) || "").toLowerCase());

  const runReport = async () => {
    setLoading(true);
    try {
      const res = await getDailyPatientReport(date);
      setRows(res.data || []);
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to load the daily report."), "error");
    } finally {
      setLoading(false);
    }
  };

  // Patient ids are GUIDs now — never print one in place of a missing OP number.
  const opNumber = (r) => patientFor(r)?.opNumber || "—";

  // Both sources ultimately derive from the patient's DateOfBirth — r.age is DATEDIFF'd
  // server-side, dob comes off the patient list — so they agree. Prefer the client-side
  // calculation because the patient list is fetched fresh here, then fall back to r.age.
  // Blank for patients registered before patientService started sending a dateOfBirth.
  const ageFor = (r) => formatAge(patientFor(r)?.dob, r.age) ?? "—";

  // `bank` is the amount collected via bank mode; `bankName` (same shape as the
  // daily-expenses report) names which bank it went into — shown alongside, not instead
  // of, the amount since a row can be bank-tagged with a 0 amount for other modes.
  const bankCell = (r) => (r.bankName ? `${fmt(r.bank)} (${r.bankName})` : fmt(r.bank));

  // Derived client-side — /api/reports/daily-patient returns only the components.
  const totalAmountOf = (r) => Number(r.treatmentAmount || 0) + Number(r.medicineAmount || 0);
  const totalPaidOf = (r) =>
    Number(r.cash || 0) + Number(r.card || 0) + Number(r.uPI || 0) + Number(r.bank || 0);

  const handleExport = () => {
    const headers = [
      "S.No", "Patient ID", "Patient Name", "Age", "Mobile No", "Address",
      "Treatment", "Shades", "Work", "Tooth", "Treatment Amount", "Medicine Amount",
      "Total Amount", "Cash", "Card", "UPI", "Bank", "Total Paid", "Bank Name", "Remarks",
    ];
    const dataRows = rows.map((r) => [
      r.slNo, opNumber(r), r.patientName, ageFor(r), r.mobileNumber, r.address,
      r.treatment, r.shades, r.work || "", r.tooth, r.treatmentAmount, r.medicineAmount,
      totalAmountOf(r), r.cash, r.card, r.uPI, r.bank, totalPaidOf(r), r.bankName || "", r.remarks || "",
    ]);
    dataRows.push([
      "", "", "", "", "", "", "", "", "", "TOTAL PATIENT RECEIPTS",
      totals.treatmentAmount, totals.medicineAmount, totals.totalAmount,
      totals.cash, totals.card, totals.upi, totals.bank, totals.totalPaid, "", "",
    ]);
    exportToCsv(`Daily-Report-${date}.csv`, headers, dataRows);
  };

  const totals = (rows || []).reduce((t, r) => ({
    treatmentAmount: t.treatmentAmount + Number(r.treatmentAmount || 0),
    medicineAmount: t.medicineAmount + Number(r.medicineAmount || 0),
    cash: t.cash + Number(r.cash || 0),
    card: t.card + Number(r.card || 0),
    upi: t.upi + Number(r.uPI || 0),
    bank: t.bank + Number(r.bank || 0),
    totalAmount: t.totalAmount + totalAmountOf(r),
    totalPaid: t.totalPaid + totalPaidOf(r),
  }), { treatmentAmount: 0, medicineAmount: 0, cash: 0, card: 0, upi: 0, bank: 0, totalAmount: 0, totalPaid: 0 });

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Daily Report</h4><h6>Patient-wise daily cash flow / collection</h6></div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-1">Date</label>
                <input type="date" className="form-control form-control-sm" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={runReport} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-outline-success" onClick={handleExport} disabled={!rows || rows.length === 0}>
                  Export to Excel
                </button>
              </div>
            </div>
          </div>
        </div>

        {rows && (
          <div className="card">
            <div className="card-body p-0">
              <StickyScrollTable>
                <table className="table table-sm table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>S.No</th>
                      <th>Patient ID</th>
                      <th>Patient Name</th>
                      <th>Age</th>
                      <th>Mobile No</th>
                      <th>Address</th>
                      <th>Treatment</th>
                      <th>Shades</th>
                      <th>Work</th>
                      <th>Tooth</th>
                      <th className="text-end">Treatment Amount</th>
                      <th className="text-end">Medicine Amount</th>
                      <th className="text-end">Total Amount</th>
                      <th className="text-end">Cash</th>
                      <th className="text-end">Card</th>
                      <th className="text-end">UPI</th>
                      <th className="text-end">Bank</th>
                      <th className="text-end">Total Paid</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={19} className="text-center text-muted py-3">No collections recorded for this date.</td></tr>
                    ) : rows.map((r) => (
                      <tr key={r.invoiceID ?? r.slNo}>
                        <td>{r.slNo}</td>
                        <td>{opNumber(r)}</td>
                        <td>{r.patientName}</td>
                        <td>{ageFor(r)}</td>
                        <td>{r.mobileNumber || "—"}</td>
                        <td>{r.address || "—"}</td>
                        <td>{r.treatment || "—"}</td>
                        <td>{r.shades || "—"}</td>
                        <td>{r.work || "—"}</td>
                        <td>{r.tooth || "—"}</td>
                        <td className="text-end">{fmt(r.treatmentAmount)}</td>
                        <td className="text-end">{fmt(r.medicineAmount)}</td>
                        <td className="text-end fw-semibold">{fmt(totalAmountOf(r))}</td>
                        <td className="text-end">{fmt(r.cash)}</td>
                        <td className="text-end">{fmt(r.card)}</td>
                        <td className="text-end">{fmt(r.uPI)}</td>
                        <td className="text-end">{bankCell(r)}</td>
                        <td className="text-end fw-semibold">{fmt(totalPaidOf(r))}</td>
                        <td>{r.remarks || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                  {rows.length > 0 && (
                    <tfoot>
                      <tr className="table-light fw-bold">
                        <td colSpan={10} className="text-end">TOTAL PATIENT RECEIPTS</td>
                        <td className="text-end">{fmt(totals.treatmentAmount)}</td>
                        <td className="text-end">{fmt(totals.medicineAmount)}</td>
                        <td className="text-end">{fmt(totals.totalAmount)}</td>
                        <td className="text-end">{fmt(totals.cash)}</td>
                        <td className="text-end">{fmt(totals.card)}</td>
                        <td className="text-end">{fmt(totals.upi)}</td>
                        <td className="text-end">{fmt(totals.bank)}</td>
                        <td className="text-end">{fmt(totals.totalPaid)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </StickyScrollTable>
            </div>
          </div>
        )}

        {!rows && !loading && (
          <div className="card text-center py-5 text-muted">
            <p>Select a date and click <strong>Search</strong> to view the daily report.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyReport;
